/*
 *  -----------------------------------------------------------------------
 *    <copyright file="manage-attendee-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { Attendee } from '@interfaces/report-templates.interface';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { useMutationAttendee } from '@apis/bff-api/report-templates';

const COMPONENT_NAME = 'manage-attendee-dialog';

export interface ManageAttendeeDialogProps {
    studyId: string;
    onClose: () => void;
    onSubmit: (attendeeId: string | undefined) => void;
    currentAttendee?: Attendee;
    forceActive?: boolean;
}

const ManageAttendeeDialog = ({
    studyId,
    onClose,
    onSubmit,
    currentAttendee,
    forceActive,
}: ManageAttendeeDialogProps) => {
    const { t } = useTranslation();

    let dialogCreatedUpdatedInfo: ReactNode | undefined;
    let dialogPrimaryButtonLabel: string;
    let dialogTitle: string;
    let dialogInitialHeight: number;

    if (currentAttendee) {
        dialogTitle = t('ra:editAttendeeId', { id: currentAttendee._itemId ?? currentAttendee.recordId });
        dialogPrimaryButtonLabel = t('common:update');
        dialogInitialHeight = 300;
        dialogCreatedUpdatedInfo = (
            <CreatedUpdatedInfo
                created={`${stringDateWithoutTimeFromUtc(currentAttendee.createdDate, 'DD-MMM-YYYY')} by ${currentAttendee.createdByName}`}
                updated={`${stringDateWithoutTimeFromUtc(currentAttendee.updatedDate, 'DD-MMM-YYYY')} by ${currentAttendee.updatedByName}`}
            />
        );
    } else {
        dialogTitle = t('ra:addAttendee');
        dialogPrimaryButtonLabel = t('common:add');
        dialogInitialHeight = 250;
    }

    const { trigger: putAttendee } = useMutationAttendee();

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<Attendee>({
        mode: 'all',
        defaultValues: {
            jobTitle: currentAttendee?.jobTitle || '',
            firstName: currentAttendee?.firstName || '',
            lastName: currentAttendee?.lastName || '',
            id: currentAttendee?.id,
            active: forceActive ? true : (currentAttendee?.active ?? true),
        },
    });

    useUnsavedChangesTracking({
        area: 'risk-assessment-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'risk-assessment-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onSubmitHandler = async (data: Attendee) => {
        const payload = {
            firstName: data.firstName,
            lastName: data.lastName,
            jobTitle: data.jobTitle,
            active: forceActive ? true : data.active,
            studyId,
            ...(data.id ? { id: data.id } : {}),
        };

        try {
            const result = await putAttendee(payload);
            ShowSuccess(t('common:actionsSuccessfulToast'));
            const [id] = result as string[];
            onSubmit(id);
        } catch {
            ShowError(t('common:errorMessage'));
            onSubmit(undefined);
        }
    };

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }
        onClose();
    };

    return (
        <DraggableDialog
            title={dialogTitle}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            dataTestId="manage-attendee-dialog"
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmitHandler)()}
            primaryBtnLabel={dialogPrimaryButtonLabel}
            initialHeight={dialogInitialHeight}
            initialWidth={927}
            createdUpdatedInfo={dialogCreatedUpdatedInfo}
            createdUpdatedBackgroundColour="gray"
        >
            <form id="add-edit-attendee-form" className="k-w-full">
                <div className="k-d-flex k-flex-auto k-gap-3 k-pb-sm">
                    <Controller
                        control={control}
                        name="active"
                        render={({ field: { onChange, value } }) => (
                            <CoreCheckbox
                                data-testid="active-attendee"
                                onChange={onChange}
                                value={value}
                                defaultChecked
                                label={t('common:active')}
                                disabled={forceActive}
                            />
                        )}
                    />
                </div>
                <div className="k-shrink-0 k-d-flex k-justify-content-center">
                    <TextBoxController
                        control={control}
                        name="jobTitle"
                        id="attendee-job-title"
                        data-testid="attendee-job-title-input"
                        label={`${t('ra:jobTitle')} *`}
                        maxLength={150}
                        required
                    />
                </div>
                <div className="k-d-flex k-justify-content-start k-gap-4">
                    <div className="!k-basis-1/2">
                        <TextBoxController
                            control={control}
                            name="firstName"
                            id="attendee-first-name"
                            data-testid="attendee-first-name-input"
                            label={`${t('firstName')} *`}
                            maxLength={70}
                            required
                        />
                    </div>
                    <div className="!k-basis-1/2">
                        <TextBoxController
                            control={control}
                            name="lastName"
                            id="attendee-last-name"
                            data-testid="last-name-input-input"
                            label={`${t('lastName')} *`}
                            maxLength={70}
                            required
                        />
                    </div>
                </div>
            </form>
        </DraggableDialog>
    );
};

export default ManageAttendeeDialog;
